/**
 * `<CentreColumnWithBanner>` — thin centre-column shell that hoists
 * the template-status banner band above the four-fork chat mounts in
 * `Workspace.tsx`.
 *
 * Why a shell. The centre column has four mutually-exclusive child
 * mounts — `<PlanModeView>`, `<ChatPanel>`, `<ChatPanelPlaceholder>`,
 * and the `aria-busy` loader. Hoisting the template banners to the
 * shell keeps them visible across all those forks.
 *
 * The `aria-busy` loader fork (`!tokensResolved`) intentionally
 * suppresses the banner band — keeps the ~50ms cold-launch loading
 * state visually clean. Callers pass `suppressBanner={true}` for the
 * loader window. Defaults to `false`.
 *
 * Flag-gating. `TemplateTooOldBanner` and `TemplateDegradedBanner`
 * gate internally on `agentTransparency` since they are part of the
 * same transparency surface family. The shell only controls whether
 * they mount at all (suppression for loader fork).
 *
 * Cross-game remount. Callers SHOULD apply `key={gameId}` on this
 * component (see Workspace.tsx) so navigation from one game to another
 * remounts the whole subtree.
 */

import { ActivityBanner } from './ActivityBanner';
import { TemplateDegradedBanner } from './TemplateDegradedBanner';
import { TemplateTooOldBanner } from './TemplateTooOldBanner';
import type { AgentRow, MainAgentRow } from '../chat/state';
import { useActivitySnapshot } from '../hooks/useActivitySnapshot';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import type { WorkspaceRunState } from '../workspace/run-state';

export interface CentreColumnWithBannerProps {
    gameId: string;
    children: React.ReactNode;
    /**
     * When `true`, the banner band stays unmounted regardless of
     * snapshot state. Workspace.tsx passes `suppressBanner={!tokensResolved}`
     * so the `aria-busy` loader fork stays visually clean. Defaults to `false`.
     */
    suppressBanner?: boolean;
    /**
     * Chat-reducer slices forwarded to `<ActivityBanner>` so the C4
     * Issue F verbose-mode drawer can mount `<MiniTranscript>` with
     * the right chunk buffer. Optional — when omitted the banner renders
     * without drawer chunks.
     */
    chatAgents?: ReadonlyMap<string, AgentRow>;
    /** Orchestrator slice — same role as `chatAgents`. */
    chatMainAgent?: MainAgentRow | null;
    runState?: WorkspaceRunState | null;
}

/**
 * Child component that owns the `useActivitySnapshot` subscription
 * and renders the template-status banners. Only mounts when the
 * loader-suppression guard passes in the parent.
 */
function BannerBand({
    gameId,
    chatAgents,
    chatMainAgent,
    runState,
}: {
    gameId: string;
    chatAgents?: ReadonlyMap<string, AgentRow>;
    chatMainAgent?: MainAgentRow | null;
    runState?: WorkspaceRunState | null;
}): React.JSX.Element {
    const snapshot = useActivitySnapshot(gameId);
    return (
        <>
            <TemplateTooOldBanner gameId={gameId} snapshot={snapshot} />
            <TemplateDegradedBanner gameId={gameId} snapshot={snapshot} />
            <ActivityBanner
                gameId={gameId}
                runState={runState}
                chatAgents={chatAgents}
                chatMainAgent={chatMainAgent}
            />
        </>
    );
}

export function CentreColumnWithBanner({
    gameId,
    children,
    suppressBanner = false,
    chatAgents,
    chatMainAgent,
    runState,
}: CentreColumnWithBannerProps): React.JSX.Element {
    const flags = useFeatureFlags();
    const showBannerBand =
        flags.loaded && flags.agentTransparency && !flags.newUi && !suppressBanner;

    return (
        <div
            className="@container/banner flex flex-1 flex-col overflow-hidden"
            data-testid="centre-column-with-banner"
        >
            {showBannerBand ? (
                <BannerBand
                    gameId={gameId}
                    chatAgents={chatAgents}
                    chatMainAgent={chatMainAgent}
                    runState={runState}
                />
            ) : null}
            {children}
        </div>
    );
}
